import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Gauge, Plus, X, ChevronRight, Power, Trash2, Pencil, Bell, ListChecks, Wand2, LogOut, LogIn, Clock, WifiOff, Battery, AlertTriangle, Cpu, Fuel, UserRound, Wrench } from "lucide-react";
import { ZoneExpress, HorairesExpress, DeconnexionExpress, BatterieExpress, DtcExpress, CarburantExpress, ConducteurExpress, classifyA9, scopeToBackend } from "@/pages/AlertExpressForms";
import MaintenanceWizard from "@/components/MaintenanceWizard";
import SimpleAlertWizard from "@/components/SimpleAlertWizard";
import AlertSettingsModal from "@/components/AlertSettingsModal";
import NotificationRulesView from "@/pages/NotificationRulesView";
import { countCaps, vehiclesInScope, hasBattery, hasDtc, hasFuel, hasDriver } from "@/lib/capabilities";

const INP = "bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]";

// ================= Création / édition EXPRESS Excès de vitesse (UN seul écran) =================
function SpeedExpress({ open, onClose, onSaved, companies, pools, vehicles, isAdmin, editing }) {
  const fleetAvailable = isAdmin || companies.length === 1;
  const [speed, setSpeed] = useState("90");
  const [scopeKind, setScopeKind] = useState(isAdmin ? "fleet" : "company");
  const [scopeId, setScopeId] = useState("");
  const [effective, setEffective] = useState(null);
  const [when, setWhen] = useState("always");
  const [days, setDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [h0, setH0] = useState(7);
  const [h1, setH1] = useState(18);
  const [saving, setSaving] = useState(false);
  const DAYS = [[0, "L"], [1, "M"], [2, "M"], [3, "J"], [4, "V"], [5, "S"], [6, "D"]];

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setScopeKind(editing.kind); setScopeId(editing.id || "");
      setSpeed(String(editing.speed ?? 90));
      if (editing.always_on) { setWhen("always"); } else { setWhen("hours"); setDays(editing.days || [0, 1, 2, 3, 4, 5, 6]); setH0(editing.hour_start ?? 7); setH1(editing.hour_end ?? 18); }
    } else {
      setScopeKind(isAdmin ? "fleet" : "company"); setScopeId(""); setSpeed("90"); setWhen("always"); setDays([0, 1, 2, 3, 4, 5, 6]); setH0(7); setH1(18);
    }
  }, [open, editing, isAdmin]);

  const loadEffective = useCallback(async () => {
    if (scopeKind !== "vehicle" || !scopeId) { setEffective(null); return; }
    try {
      const r = await api.get(`/alert-settings?scope_type=vehicle&scope_id=${encodeURIComponent(scopeId)}`);
      const row = (r.data || []).find((x) => x.type === "exces_vitesse");
      if (row) {
        const eff = row.effective_speed_limit_kmh != null ? row.effective_speed_limit_kmh : (row.params?.speed_limit_kmh ?? 90);
        setEffective(eff);
        if (!editing && !row.has_override) setSpeed(String(eff));
      }
    } catch { setEffective(null); }
  }, [scopeKind, scopeId, editing]);
  useEffect(() => { if (open) loadEffective(); }, [open, loadEffective]);

  if (!open) return null;
  const scopeChosen = scopeKind === "fleet" || !!scopeId;
  const speedNum = Number(speed);
  const speedValid = speedNum >= 10 && speedNum <= 300;

  const save = async () => {
    setSaving(true);
    try {
      const { scope_type, scope_id } = scopeToBackend({ kind: scopeKind, id: scopeId }, isAdmin, companies);
      const body = { active: true, always_on: when === "always", params: { speed_limit_kmh: Math.round(speedNum) } };
      if (when === "hours") { body.days = days; body.hour_start = Number(h0); body.hour_end = Number(h1); }
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      await api.put(`/alert-settings/exces_vitesse?${qs}`, body);
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6" data-testid="speed-express" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg my-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-extrabold text-lg flex items-center gap-2"><Gauge className="w-5 h-5 text-[#EF4444]" /> Excès de vitesse</h2>
          <button onClick={onClose} data-testid="express-close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <p className="text-base font-bold mb-2">À partir de quelle vitesse souhaitez-vous être alerté ?</p>
            <div className="flex items-end gap-3">
              <input type="number" min="10" max="300" value={speed} autoFocus data-testid="express-speed"
                onChange={(e) => setSpeed(e.target.value)}
                className="bg-secondary/60 border-2 border-[#17A2B8]/40 focus:border-[#17A2B8] rounded-xl px-4 py-3 text-4xl font-extrabold text-center w-36 outline-none" />
              <span className="text-xl font-bold pb-3">km/h</span>
            </div>
            {!speedValid && <p className="text-xs text-[#EF4444] mt-1">Valeur autorisée : 10 à 300 km/h.</p>}
            {effective != null && <p className="text-xs text-muted-foreground mt-1" data-testid="express-current-limit">Limite actuelle : {effective} km/h</p>}
          </div>
          <div>
            <p className="label-mono mb-1.5">Appliquer à</p>
            <div className="flex gap-2 flex-wrap">
              <select value={scopeKind} onChange={(e) => { setScopeKind(e.target.value); setScopeId(""); }} data-testid="express-scope-kind" className={INP} disabled={!!editing}>
                {fleetAvailable && <option value="fleet">Toute ma flotte</option>}
                <option value="company">Une société</option>
                <option value="pool">Un groupe</option>
                <option value="vehicle">Un véhicule</option>
              </select>
              {scopeKind === "company" && <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="express-scope-entity" className={INP} disabled={!!editing}><option value="">— Société —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
              {scopeKind === "pool" && <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="express-scope-entity" className={INP} disabled={!!editing}><option value="">— Groupe —</option>{pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
              {scopeKind === "vehicle" && <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="express-scope-entity" className={INP} disabled={!!editing}><option value="">— Véhicule —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}</select>}
            </div>
          </div>
          <div>
            <p className="label-mono mb-1.5">Quand</p>
            <select value={when} onChange={(e) => setWhen(e.target.value)} data-testid="express-when" className={INP}>
              <option value="always">Tout le temps</option>
              <option value="hours">Horaires personnalisés</option>
            </select>
            {when === "hours" && (
              <div className="mt-2 space-y-2 pl-1">
                <div className="flex gap-1">{DAYS.map(([d, l]) => { const on = days.includes(d); return <button key={d} type="button" data-testid={`express-day-${d}`} onClick={() => setDays((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d].sort())} className={`text-xs w-8 h-8 rounded border ${on ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground"}`}>{l}</button>; })}</div>
                <div className="flex items-center gap-2"><input type="number" min="0" max="24" value={h0} onChange={(e) => setH0(e.target.value)} data-testid="express-h0" className={`${INP} w-20`} /><span className="text-sm">h →</span><input type="number" min="0" max="24" value={h1} onChange={(e) => setH1(e.target.value)} data-testid="express-h1" className={`${INP} w-20`} /><span className="text-sm">h</span></div>
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end">
          <button onClick={save} disabled={saving || !speedValid || !scopeChosen} data-testid="express-create"
            className="bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-md text-sm">
            {editing ? "Enregistrer" : "Créer l'alerte"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modèles proposés dans le sélecteur « + Créer une alerte » (A10.2).
const PICKS = [
  { key: "speed", icon: Gauge, color: "#EF4444", title: "Excès de vitesse", desc: "Être alerté au-dessus d'une vitesse" },
  { key: "zone-sortie", icon: LogOut, color: "#17A2B8", title: "Sortie de zone", desc: "Être alerté quand un véhicule quitte une zone" },
  { key: "zone-entree", icon: LogIn, color: "#17A2B8", title: "Entrée de zone", desc: "Être alerté quand un véhicule entre dans une zone" },
  { key: "horaires", icon: Clock, color: "#F5821F", title: "Utilisation hors horaires", desc: "Être alerté en dehors des horaires autorisés" },
  { key: "offline", icon: WifiOff, color: "#EF4444", title: "Traceur hors ligne", desc: "Être alerté si un traceur ne communique plus" },
];

// ================= Vue principale =================
export default function AlertRulesView({ onGoEvents, onAdvanced }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user && ["admin", "gestionnaire"].includes(user.role);
  const [companies, setCompanies] = useState([]);
  const [pools, setPools] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [zones, setZones] = useState([]);
  const [speedRules, setSpeedRules] = useState([]);
  const [offlineRules, setOfflineRules] = useState([]);
  const [batterieRules, setBatterieRules] = useState([]);
  const [dtcRules, setDtcRules] = useState([]);
  const [fuelLowRules, setFuelLowRules] = useState([]);
  const [fuelDropRules, setFuelDropRules] = useState([]);
  const [driverRules, setDriverRules] = useState([]);
  const [zoneRules, setZoneRules] = useState([]);
  const [horaireRules, setHoraireRules] = useState([]);
  const [advRules, setAdvRules] = useState([]);
  const [recent, setRecent] = useState({ count: 0, items: [] });
  const [picker, setPicker] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [modal, setModal] = useState({ type: null, editing: null });
  const [loading, setLoading] = useState(true);
  const [ruleQuery, setRuleQuery] = useState("");
  const [ruleType, setRuleType] = useState("all");
  // P0.1 — vue des alertes EFFECTIVES (actives par défaut, même sans override)
  const [catScope, setCatScope] = useState({ kind: isAdmin ? "fleet" : "company", id: "" });
  const [catalog, setCatalog] = useState([]);
  const [catStatus, setCatStatus] = useState("all");
  const [catLoading, setCatLoading] = useState(false);
  // P0.3.1 — indicateur Maintenance (opt-in : basé sur les échéances planifiées, pas de toggle global)
  const [maintSummary, setMaintSummary] = useState({ total: 0, warn: 0 });
  const [maintWizard, setMaintWizard] = useState(false);
  const loadMaint = useCallback(() => {
    api.get("/maintenance/overview").then((r) => {
      const rows = r.data || [];
      setMaintSummary({ total: rows.length, warn: rows.filter((x) => x.state === "due" || x.state === "overdue").length });
    }).catch(() => {});
  }, []);
  useEffect(() => { loadMaint(); }, [loadMaint]);

  const label = (kind, id) => kind === "global" || kind === "fleet" ? "Toute la flotte"
    : kind === "company" ? (companies.find((c) => c.id === id)?.name || "Société")
    : kind === "pool" ? (pools.find((p) => p.id === id)?.name || "Groupe")
    : (vehicles.find((v) => v.id === id)?.name || "Véhicule");
  const zoneName = (id) => zones.find((z) => z.id === id)?.name || "Zone";
  const schedLabelA4 = (r) => r.always_on ? "24h/24" : `${String(r.hour_start).padStart(2, "0")}h–${String(r.hour_end).padStart(2, "0")}h`;
  const a9SchedLabel = (c) => c.when === "hours" || c.type === "horaires"
    ? `${String(c.h0).padStart(2, "0")}h–${String(c.h1).padStart(2, "0")}h`
    : "24h/24";

  // A10.3 — capacités calculées depuis la liste /vehicles DÉJÀ chargée (aucune requête, aucun N+1).
  const fleetCaps = useMemo(() => countCaps(vehicles), [vehicles]);
  const compatLabel = (kind, id, pred) => {
    const scoped = vehiclesInScope(vehicles, kind === "fleet" ? "fleet" : kind, id, pools);
    if (!scoped.length) return null;
    const n = scoped.filter(pred).length;
    return n < scoped.length ? `${n}/${scoped.length} compatibles` : `${n} compatible${n > 1 ? "s" : ""}`;
  };

  const loadRefs = useCallback(async () => {
    const [c, p, v, z] = await Promise.all([
      api.get("/companies").then((r) => r.data).catch(() => []),
      api.get("/pools").then((r) => r.data).catch(() => []),
      api.get("/vehicles").then((r) => r.data).catch(() => []),
      api.get("/geofences").then((r) => r.data).catch(() => []),
    ]);
    setCompanies(c); setPools(p); setVehicles(v); setZones(z);
  }, []);

  // A10.1b — agrégation SCALABLE : 1 appel /alert-settings/rules (A4) + 1 appel /alert-rules (A9.1). Aucun appel par véhicule.
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/alert-settings/rules");
      const rows = r.data || [];
      setSpeedRules(rows.filter((x) => x.type === "exces_vitesse").map((x) => ({
        kind: x.scope_type, id: x.scope_id, name: x.entity_name,
        speed: x.params?.speed_limit_kmh ?? 90, active: x.active !== false,
        always_on: x.always_on !== false, days: x.days, hour_start: x.hour_start ?? 0, hour_end: x.hour_end ?? 24,
      })));
      setOfflineRules(rows.filter((x) => x.type === "deconnexion").map((x) => ({
        kind: x.scope_type, id: x.scope_id, name: x.entity_name,
        minutes: Math.round((x.params?.offline_after_s ?? 300) / 60), active: x.active !== false,
      })));
      setBatterieRules(rows.filter((x) => x.type === "batterie").map((x) => ({
        kind: x.scope_type, id: x.scope_id, name: x.entity_name,
        openV: (x.params?.open_below_mv ?? 11000) / 1000, closeV: (x.params?.close_above_mv ?? 11500) / 1000, active: x.active !== false,
      })));
      setDtcRules(rows.filter((x) => x.type === "dtc").map((x) => ({
        kind: x.scope_type, id: x.scope_id, name: x.entity_name, active: x.active !== false,
      })));
      setFuelLowRules(rows.filter((x) => x.type === "carburant_bas").map((x) => ({
        kind: x.scope_type, id: x.scope_id, name: x.entity_name, active: x.active !== false,
        lowPct: x.params?.fuel_low_pct ?? 15,
      })));
      setFuelDropRules(rows.filter((x) => x.type === "baisse_carburant").map((x) => ({
        kind: x.scope_type, id: x.scope_id, name: x.entity_name, active: x.active !== false,
        dropPct: x.params?.fuel_drop_pct ?? 10, dropWin: x.params?.fuel_drop_window_min ?? 30,
      })));
      setDriverRules(rows.filter((x) => x.type === "conducteur_non_identifie").map((x) => ({
        kind: x.scope_type, id: x.scope_id, name: x.entity_name, active: x.active !== false,
        minutes: Math.round((x.params?.driver_grace_s ?? 60) / 60),
      })));
    } catch { setSpeedRules([]); setOfflineRules([]); setBatterieRules([]); setDtcRules([]); setFuelLowRules([]); setFuelDropRules([]); setDriverRules([]); }
    try {
      const r2 = await api.get("/alert-rules");
      const zone = [], hor = [], adv = [];
      (r2.data || []).forEach((rule) => {
        const c = classifyA9(rule);
        if (c.type === "zone") zone.push(c);
        else if (c.type === "horaires") hor.push(c);
        else adv.push(rule);
      });
      setZoneRules(zone); setHoraireRules(hor); setAdvRules(adv);
    } catch { setZoneRules([]); setHoraireRules([]); setAdvRules([]); }
    api.get("/alerts?limit=5&acknowledged=false").then((r) => setRecent({ count: r.data.length, items: r.data.slice(0, 4) })).catch(() => {});
    setLoading(false);
  }, []);

  useEffect(() => { loadRefs(); loadRules(); /* une seule passe, aucun polling */ }, []); // eslint-disable-line

  // P0.1 — catalogue effectif : 1 requête bornée par changement de portée (aucun polling, aucun N+1).
  const loadCatalog = useCallback(async () => {
    const { scope_type, scope_id } = scopeToBackend(catScope, isAdmin, companies);
    if (scope_type !== "global" && !scope_id) { setCatalog([]); return; }
    setCatLoading(true);
    try {
      const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
      const r = await api.get(`/alert-settings/catalog?${qs}`);
      setCatalog(r.data || []);
    } catch { setCatalog([]); }
    setCatLoading(false);
  }, [catScope, isAdmin, companies]);
  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const catToggle = async (row) => {
    const { scope_type, scope_id } = scopeToBackend(catScope, isAdmin, companies);
    const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
    try {
      await api.put(`/alert-settings/${row.type}?${qs}`, { active: !row.active });
      toast.success(row.active ? "Alerte désactivée" : "Alerte réactivée");
      loadCatalog();
    } catch { toast.error("Erreur"); }
  };
  const catReset = async (row) => {
    if (!window.confirm(`Réinitialiser « ${row.label} » ? L'override de ce niveau sera supprimé et l'alerte reviendra au réglage hérité (niveau supérieur ou défaut système).`)) return;
    const { scope_type, scope_id } = scopeToBackend(catScope, isAdmin, companies);
    const qs = `scope_type=${scope_type}` + (scope_id ? `&scope_id=${encodeURIComponent(scope_id)}` : "");
    try {
      await api.delete(`/alert-settings/${row.type}/override?${qs}`);
      toast.success("Réinitialisée (retour à l'héritage)");
      loadCatalog();
    } catch { toast.error("Erreur"); }
  };

  const openCreate = (type) => { setPicker(false); setModal({ type, editing: null }); };
  const closeModal = () => setModal({ type: null, editing: null });

  // --- A4 (exces_vitesse / deconnexion / batterie / dtc) : toggle + reset override ---
  const toggleA4 = async (type, r) => {
    const qs = `scope_type=${r.kind}` + (r.id ? `&scope_id=${encodeURIComponent(r.id)}` : "");
    let body = { active: !r.active, always_on: r.always_on ?? true };
    if (type === "exces_vitesse") body = { active: !r.active, always_on: r.always_on, params: { speed_limit_kmh: r.speed } };
    else if (type === "deconnexion") body = { active: !r.active, always_on: true, params: { offline_after_s: r.minutes * 60 } };
    else if (type === "batterie") body = { active: !r.active, always_on: true, params: { open_below_mv: Math.round(r.openV * 1000), close_above_mv: Math.round(r.closeV * 1000) } };
    else if (type === "dtc") body = { active: !r.active, always_on: true };
    else if (type === "carburant_bas") body = { active: !r.active, always_on: true, params: { fuel_low_pct: r.lowPct, fuel_recover_pct: Math.min(100, r.lowPct + 5) } };
    else if (type === "baisse_carburant") body = { active: !r.active, always_on: true, params: { fuel_drop_pct: r.dropPct, fuel_drop_window_min: r.dropWin } };
    else if (type === "conducteur_non_identifie") body = { active: !r.active, always_on: true, params: { driver_grace_s: r.minutes * 60 } };
    try { await api.put(`/alert-settings/${type}?${qs}`, body); toast.success(r.active ? "Alerte désactivée" : "Alerte réactivée"); loadRules(); }
    catch { toast.error("Erreur"); }
  };
  const resetA4 = async (type, r) => {
    const qs = `scope_type=${r.kind}` + (r.id ? `&scope_id=${encodeURIComponent(r.id)}` : "");
    try { await api.delete(`/alert-settings/${type}/override?${qs}`); toast.success("Alerte supprimée"); loadRules(); }
    catch { toast.error("Erreur"); }
  };

  // --- A9.1 (zone / horaires) : toggle enabled + delete ---
  const toggleA9 = async (raw) => {
    const body = { name: raw.name, message: raw.message || null, severity: raw.severity || "medium",
      enabled: !raw.enabled, scope_type: raw.scope_type ?? null, scope_id: raw.scope_id ?? null,
      groups: raw.groups, duration_s: raw.duration_s || 0 };
    if (raw.scope_type == null) { body.company_id = raw.company_id || null; body.vehicle_ids = raw.vehicle_ids || []; }
    try { await api.put(`/alert-rules/${raw.id}`, body); toast.success(raw.enabled ? "Alerte désactivée" : "Alerte réactivée"); loadRules(); }
    catch { toast.error("Erreur"); }
  };
  const delA9 = async (id) => { try { await api.delete(`/alert-rules/${id}`); toast.success("Règle supprimée"); loadRules(); } catch { toast.error("Erreur"); } };

  const empty = !loading && speedRules.length === 0 && offlineRules.length === 0 && batterieRules.length === 0 && dtcRules.length === 0 && fuelLowRules.length === 0 && fuelDropRules.length === 0 && driverRules.length === 0 && zoneRules.length === 0 && horaireRules.length === 0 && advRules.length === 0;

  // §9 — filtrage simple (type + recherche texte), calculé sur les listes déjà chargées (aucune requête).
  const totalRules = speedRules.length + zoneRules.length + horaireRules.length + offlineRules.length + batterieRules.length + dtcRules.length + fuelLowRules.length + fuelDropRules.length + driverRules.length + advRules.length;
  const mType = (t) => ruleType === "all" || ruleType === t;
  const mText = (s) => !ruleQuery.trim() || s.toLowerCase().includes(ruleQuery.trim().toLowerCase());
  const fSpeed = speedRules.filter((r) => mType("speed") && mText(`excès de vitesse ${r.speed} ${r.name || label(r.kind, r.id)}`));
  const fZone = zoneRules.filter((c) => mType("zone") && mText(`${c.mode === "entree" ? "entrée" : "sortie"} de zone ${zoneName(c.zone_id)} ${label(c.scopeKind, c.scopeId)}`));
  const fHoraires = horaireRules.filter((c) => mType("horaires") && mText(`hors horaires ${label(c.scopeKind, c.scopeId)}`));
  const fOffline = offlineRules.filter((r) => mType("offline") && mText(`traceur hors ligne ${r.minutes} ${r.name || label(r.kind, r.id)}`));
  const fBatterie = batterieRules.filter((r) => mType("batterie") && mText(`batterie faible ${r.openV} ${r.name || label(r.kind, r.id)}`));
  const fDtc = dtcRules.filter((r) => mType("dtc") && mText(`défaut moteur dtc ${r.name || label(r.kind, r.id)}`));
  const fFuelLow = fuelLowRules.filter((r) => mType("carburant") && mText(`carburant bas niveau ${r.lowPct} ${r.name || label(r.kind, r.id)}`));
  const fFuelDrop = fuelDropRules.filter((r) => mType("carburant") && mText(`baisse carburant ${r.dropPct} ${r.name || label(r.kind, r.id)}`));
  const fDriver = driverRules.filter((r) => mType("conducteur") && mText(`conducteur non identifié ${r.name || label(r.kind, r.id)}`));
  const fAdv = advRules.filter((r) => mType("advanced") && mText(`${r.name || ""} règle personnalisée avancée`));
  const anyVisible = fSpeed.length + fZone.length + fHoraires.length + fOffline.length + fBatterie.length + fDtc.length + fFuelLow.length + fFuelDrop.length + fDriver.length + fAdv.length > 0;
  const showFilter = totalRules >= 5;
  const TYPE_OPTS = [["all", "Tous les types"], ["speed", "Excès de vitesse"], ["zone", "Zone"], ["horaires", "Hors horaires"], ["offline", "Traceur hors ligne"], ["batterie", "Batterie"], ["dtc", "Défaut moteur"], ["carburant", "Carburant"], ["conducteur", "Conducteur"], ["advanced", "Avancées"]];

  // P0.1 — métadonnées d'affichage du catalogue effectif
  const CAT_META = {
    exces_vitesse: { icon: Gauge, color: "#EF4444", thr: (p) => `${p.speed_limit_kmh ?? 90} km/h` },
    deconnexion: { icon: WifiOff, color: "#EF4444", thr: (p) => `${Math.round((p.offline_after_s ?? 300) / 60)} min` },
    contact: { icon: Power, color: "#17A2B8", thr: () => null },
    arret_prolonge: { icon: Clock, color: "#F59E0B", thr: (p) => `${Math.round((p.min_duration_s ?? 900) / 60)} min` },
    ralenti: { icon: Clock, color: "#F59E0B", thr: (p) => `${Math.round((p.min_duration_s ?? 300) / 60)} min` },
    exces_route: { icon: AlertTriangle, color: "#F5821F", thr: (p) => `marge +${p.margin_kmh ?? 0} km/h · route OSM` },
    batterie: { icon: Battery, color: "#F5821F", thr: (p) => `${((p.open_below_mv ?? 11000) / 1000).toFixed(1)} V` },
    dtc: { icon: Cpu, color: "#EF4444", thr: () => null },
    carburant_bas: { icon: Fuel, color: "#0EA5E9", thr: (p) => `${p.fuel_low_pct ?? 15} %` },
    baisse_carburant: { icon: Fuel, color: "#0EA5E9", thr: (p) => `${p.fuel_drop_pct ?? 10} % / ${p.fuel_drop_window_min ?? 30} min` },
    conducteur_non_identifie: { icon: UserRound, color: "#A855F7", thr: (p) => `${Math.round((p.driver_grace_s ?? 60) / 60)} min` },
  };
  const CAP_PRED = { battery: hasBattery, dtc: hasDtc, fuel: hasFuel, driver: hasDriver };
  const catScoped = useMemo(() => vehiclesInScope(vehicles, catScope.kind, catScope.id, pools), [vehicles, catScope, pools]);
  const sourceLabel = (row) => row.source_level === "default" ? "Défaut système"
    : row.source_level === "global" ? "Toute la flotte"
    : row.source_level === "company" ? (companies.find((c) => c.id === row.source_scope_id)?.name || "Société")
    : row.source_level === "pool" ? (pools.find((p) => p.id === row.source_scope_id)?.name || "Groupe")
    : (vehicles.find((v) => v.id === row.source_scope_id)?.name || "Véhicule");
  const catFiltered = catalog.filter((r) => catStatus === "all" || (catStatus === "active" ? r.active : !r.active));

  const RuleCard = ({ icon: Icon, color, title, sub, active, onEdit, onToggle, onDelete, testid, deleteLabel }) => (
    <div className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={testid}>
      <Icon className="w-5 h-5 shrink-0" style={{ color }} />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-sm">{title}</p>
        <p className="text-xs text-muted-foreground truncate">{sub}</p>
      </div>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${active ? "text-[#10B981] bg-[#10B981]/15" : "text-muted-foreground bg-secondary"}`} data-testid={`${testid}-status`}>{active ? "ACTIVE" : "DÉSACTIVÉE"}</span>
      {canManage && (
        <div className="flex items-center gap-1">
          {onEdit && <button onClick={onEdit} data-testid={`${testid}-edit`} title="Modifier" className="p-1.5 text-muted-foreground hover:text-[#17A2B8]"><Pencil className="w-4 h-4" /></button>}
          <button onClick={onToggle} data-testid={`${testid}-toggle`} title={active ? "Désactiver" : "Réactiver"} className="p-1.5 text-muted-foreground hover:text-[#F5821F]"><Power className="w-4 h-4" /></button>
          <button onClick={onDelete} data-testid={`${testid}-delete`} title={deleteLabel || "Supprimer"} className="p-1.5 text-muted-foreground hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="alert-rules-view">
      <SpeedExpress open={modal.type === "speed"} onClose={closeModal} onSaved={loadRules} editing={modal.type === "speed" ? modal.editing : null}
        companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <ZoneExpress open={modal.type === "zone-entree" || modal.type === "zone-sortie"} mode={modal.type === "zone-entree" ? "entree" : "sortie"}
        onClose={closeModal} onSaved={loadRules} editing={(modal.type === "zone-entree" || modal.type === "zone-sortie") ? modal.editing : null}
        zones={zones} companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <HorairesExpress open={modal.type === "horaires"} onClose={closeModal} onSaved={loadRules} editing={modal.type === "horaires" ? modal.editing : null}
        companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <DeconnexionExpress open={modal.type === "offline"} onClose={closeModal} onSaved={loadRules} editing={modal.type === "offline" ? modal.editing : null}
        companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <BatterieExpress open={modal.type === "batterie"} onClose={closeModal} onSaved={loadRules} editing={modal.type === "batterie" ? modal.editing : null}
        companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <DtcExpress open={modal.type === "dtc"} onClose={closeModal} onSaved={loadRules} editing={modal.type === "dtc" ? modal.editing : null}
        companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <CarburantExpress open={modal.type === "carburant"} onClose={closeModal} onSaved={loadRules} editing={modal.type === "carburant" ? modal.editing : null}
        companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <ConducteurExpress open={modal.type === "conducteur"} onClose={closeModal} onSaved={loadRules} editing={modal.type === "conducteur" ? modal.editing : null}
        companies={companies} pools={pools} vehicles={vehicles} isAdmin={isAdmin} />
      <MaintenanceWizard open={maintWizard} onClose={() => setMaintWizard(false)} onCreated={loadMaint} vehicles={vehicles} />
      <SimpleAlertWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={() => { loadRules(); loadCatalog(); }} onAdvanced={onAdvanced}
        companies={companies} pools={pools} vehicles={vehicles} zones={zones} isAdmin={isAdmin} />

      {/* ZONE 1 — Alertes en cours : désormais couvert par l'onglet « À traiter » (évite la redondance). */}

      {/* ZONE 1bis — P0.1 : toutes les alertes ACTIVES (réglages effectifs, même sans override) */}
      <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="zone-effective">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-wrap gap-2">
          <h2 className="font-bold flex items-center gap-2"><Power className="w-4 h-4 brand-teal" /> Toutes les alertes actives</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {canManage && <button onClick={() => setManageOpen(true)} data-testid="manage-alerts-btn" className="border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-3 py-1.5 rounded-md text-sm">Gérer les alertes</button>}
            <AlertSettingsModal open={manageOpen} onClose={() => { setManageOpen(false); loadCatalog(); }} />
            <select value={catScope.kind} data-testid="cat-scope-kind"
              onChange={(e) => setCatScope({ kind: e.target.value, id: "" })}
              className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8]">
              {isAdmin && <option value="fleet">Toute la flotte</option>}
              <option value="company">Une société</option>
              <option value="pool">Un groupe</option>
              <option value="vehicle">Un véhicule</option>
            </select>
            {catScope.kind === "company" && <select value={catScope.id} data-testid="cat-scope-entity" onChange={(e) => setCatScope((s) => ({ ...s, id: e.target.value }))} className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8] max-w-[180px]"><option value="">— Société —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
            {catScope.kind === "pool" && <select value={catScope.id} data-testid="cat-scope-entity" onChange={(e) => setCatScope((s) => ({ ...s, id: e.target.value }))} className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8] max-w-[180px]"><option value="">— Groupe —</option>{pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
            {catScope.kind === "vehicle" && <select value={catScope.id} data-testid="cat-scope-entity" onChange={(e) => setCatScope((s) => ({ ...s, id: e.target.value }))} className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8] max-w-[180px]"><option value="">— Véhicule —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}</select>}
            <select value={catStatus} data-testid="cat-status-filter" onChange={(e) => setCatStatus(e.target.value)} className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8]">
              <option value="all">Toutes</option>
              <option value="active">Actives</option>
              <option value="inactive">Désactivées</option>
            </select>
          </div>
        </div>
        <div className="divide-y divide-border" data-testid="cat-list">
          {catLoading && <p className="text-sm text-muted-foreground py-6 text-center">Chargement…</p>}
          {!catLoading && (catScope.kind !== "fleet" && !catScope.id) && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="cat-pick-scope">Choisissez une portée pour voir les réglages effectifs.</p>}
          {!catLoading && (catScope.kind === "fleet" || catScope.id) && catFiltered.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="cat-empty">Aucune alerte pour ce filtre.</p>}
          {!catLoading && catFiltered.map((row) => {
            const meta = CAT_META[row.type] || { icon: Bell, color: "#17A2B8", thr: () => null };
            const Icon = meta.icon;
            const thr = meta.thr(row.params || {});
            const capN = row.capability ? catScoped.filter(CAP_PRED[row.capability]).length : null;
            const unavailable = row.capability && catScoped.length > 0 && capN === 0;
            return (
              <div key={row.type} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`cat-row-${row.type}`}>
                <Icon className="w-5 h-5 shrink-0" style={{ color: meta.color }} />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm">{row.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {thr ? `${thr} · ` : ""}Source : {sourceLabel(row)}
                    {unavailable && <span className="text-[#F5821F]"> · Non disponible sur ces véhicules</span>}
                    {!unavailable && row.capability && capN != null && catScoped.length > 0 && <span> · {capN}/{catScoped.length} compatibles</span>}
                  </p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${row.active ? "text-[#10B981] bg-[#10B981]/15" : "text-muted-foreground bg-secondary"}`} data-testid={`cat-status-${row.type}`}>{row.active ? "ACTIVÉE" : "DÉSACTIVÉE"}</span>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => catToggle(row)} data-testid={`cat-toggle-${row.type}`} title={row.active ? "Désactiver" : "Réactiver"} className="p-1.5 text-muted-foreground hover:text-[#F5821F]"><Power className="w-4 h-4" /></button>
                    {row.has_override_here && <button onClick={() => catReset(row)} data-testid={`cat-reset-${row.type}`} title="Réinitialiser (revenir à l'héritage)" className="p-1.5 text-muted-foreground hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* P0.3.1 — Indicateur Maintenance (OPT-IN, aucun toggle global) */}
      <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="maint-indicator">
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
          <Wrench className="w-5 h-5 shrink-0 text-[#F5821F]" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm flex items-center gap-2">Maintenance</p>
            {maintSummary.total === 0
              ? <p className="text-xs text-muted-foreground" data-testid="maint-indicator-empty">Aucune échéance configurée</p>
              : <p className="text-xs text-muted-foreground" data-testid="maint-indicator-count">{maintSummary.total} entretien{maintSummary.total > 1 ? "s" : ""} surveillé{maintSummary.total > 1 ? "s" : ""}{maintSummary.warn > 0 ? ` · ${maintSummary.warn} à échéance / en retard` : ""}</p>}
          </div>
          {maintSummary.warn > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded text-[#EF4444] bg-[#EF4444]/15" data-testid="maint-indicator-badge">{maintSummary.warn}</span>}
          {canManage && <button onClick={() => setMaintWizard(true)} data-testid="maint-plan-btn" className="flex items-center gap-1 text-xs font-bold text-white bg-[#F5821F] hover:bg-[#e0741a] px-3 py-1.5 rounded-md"><Plus className="w-3.5 h-3.5" /> Planifier un entretien</button>}
          {maintSummary.total > 0 && <a href="/maintenance" data-testid="maint-indicator-link" className="text-xs text-[#17A2B8] font-semibold hover:underline flex items-center gap-1">Gérer <ChevronRight className="w-3.5 h-3.5" /></a>}
        </div>
      </div>

      {/* ZONE 2 — Mes règles d'alerte */}
      <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="zone-rules">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-bold flex items-center gap-2"><ListChecks className="w-4 h-4 brand-teal" /> Mes règles d'alerte</h2>
          {canManage && <button onClick={() => setWizardOpen(true)} data-testid="create-alert-btn" className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] text-white text-sm font-bold px-4 py-2 rounded-md"><Plus className="w-4 h-4" /> Créer une alerte</button>}
        </div>
        {showFilter && (
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/30 flex-wrap" data-testid="rules-filter">
            <input value={ruleQuery} onChange={(e) => setRuleQuery(e.target.value)} data-testid="rules-search" placeholder="Rechercher une règle…"
              className="bg-secondary/60 border border-border rounded-md px-3 py-1.5 text-sm outline-none focus:border-[#17A2B8] flex-1 min-w-[160px]" />
            <select value={ruleType} onChange={(e) => setRuleType(e.target.value)} data-testid="rules-type-filter" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#17A2B8]">
              {TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}
        <div className="divide-y divide-border">
          {loading && <p className="text-sm text-muted-foreground py-6 text-center">Chargement…</p>}
          {empty && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="rules-empty">Aucune règle configurée. Cliquez sur « Créer une alerte ».</p>}
          {!loading && !empty && !anyVisible && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="rules-no-match">Aucune règle ne correspond à votre recherche.</p>}

          {fSpeed.map((r, i) => (
            <RuleCard key={`s-${r.kind}-${r.id}`} testid={`rule-speed-${i}`} icon={Gauge} color="#EF4444" active={r.active}
              title="Excès de vitesse" sub={`${r.speed} km/h · ${r.name || label(r.kind, r.id)} · ${schedLabelA4(r)}`}
              onEdit={() => setModal({ type: "speed", editing: r })} onToggle={() => toggleA4("exces_vitesse", r)} onDelete={() => resetA4("exces_vitesse", r)} />
          ))}
          {fZone.map((c, i) => (
            <RuleCard key={`z-${c.raw.id}`} testid={`rule-zone-${i}`} icon={c.mode === "entree" ? LogIn : LogOut} color="#17A2B8" active={c.raw.enabled}
              title={c.mode === "entree" ? "Entrée de zone" : "Sortie de zone"} sub={`${zoneName(c.zone_id)} · ${label(c.scopeKind, c.scopeId)} · ${a9SchedLabel(c)}`}
              onEdit={() => setModal({ type: c.mode === "entree" ? "zone-entree" : "zone-sortie", editing: c })} onToggle={() => toggleA9(c.raw)} onDelete={() => delA9(c.raw.id)} />
          ))}
          {fHoraires.map((c, i) => (
            <RuleCard key={`h-${c.raw.id}`} testid={`rule-horaires-${i}`} icon={Clock} color="#F5821F" active={c.raw.enabled}
              title="Utilisation hors horaires" sub={`${String(c.h0).padStart(2, "0")}h–${String(c.h1).padStart(2, "0")}h · ${label(c.scopeKind, c.scopeId)}`}
              onEdit={() => setModal({ type: "horaires", editing: c })} onToggle={() => toggleA9(c.raw)} onDelete={() => delA9(c.raw.id)} />
          ))}
          {fOffline.map((r, i) => (
            <RuleCard key={`o-${r.kind}-${r.id}`} testid={`rule-offline-${i}`} icon={WifiOff} color="#EF4444" active={r.active}
              title="Traceur hors ligne" sub={`${r.minutes} min · ${r.name || label(r.kind, r.id)}`}
              onEdit={() => setModal({ type: "offline", editing: { scopeKind: r.kind === "global" ? "fleet" : r.kind, scopeId: r.id, minutes: r.minutes } })}
              onToggle={() => toggleA4("deconnexion", r)} onDelete={() => resetA4("deconnexion", r)} deleteLabel="Réinitialiser" />
          ))}
          {fBatterie.map((r, i) => {
            const cl = compatLabel(r.kind, r.id, hasBattery);
            return (
              <RuleCard key={`b-${r.kind}-${r.id}`} testid={`rule-batterie-${i}`} icon={Battery} color="#F5821F" active={r.active}
                title="Batterie faible" sub={`${r.openV} V · ${r.name || label(r.kind, r.id)}${cl ? ` · ${cl}` : ""}`}
                onEdit={() => setModal({ type: "batterie", editing: { scopeKind: r.kind === "global" ? "fleet" : r.kind, scopeId: r.id, openV: r.openV, closeV: r.closeV } })}
                onToggle={() => toggleA4("batterie", r)} onDelete={() => resetA4("batterie", r)} deleteLabel="Réinitialiser" />
            );
          })}
          {fDtc.map((r, i) => {
            const cl = compatLabel(r.kind, r.id, hasDtc);
            return (
              <RuleCard key={`d-${r.kind}-${r.id}`} testid={`rule-dtc-${i}`} icon={AlertTriangle} color="#EF4444" active={r.active}
                title="Défaut moteur" sub={`${r.name || label(r.kind, r.id)}${cl ? ` · ${cl}` : ""}`}
                onEdit={() => setModal({ type: "dtc", editing: { scopeKind: r.kind === "global" ? "fleet" : r.kind, scopeId: r.id } })}
                onToggle={() => toggleA4("dtc", r)} onDelete={() => resetA4("dtc", r)} deleteLabel="Réinitialiser" />
            );
          })}
          {fFuelLow.map((r, i) => {
            const cl = compatLabel(r.kind, r.id, hasFuel);
            return (
              <RuleCard key={`fl-${r.kind}-${r.id}`} testid={`rule-carburant-bas-${i}`} icon={Fuel} color="#17A2B8" active={r.active}
                title="Niveau carburant bas" sub={`${r.lowPct} % · ${r.name || label(r.kind, r.id)}${cl ? ` · ${cl}` : ""}`}
                onEdit={() => setModal({ type: "carburant", editing: { mode: "bas", scopeKind: r.kind === "global" ? "fleet" : r.kind, scopeId: r.id, lowPct: r.lowPct } })}
                onToggle={() => toggleA4("carburant_bas", r)} onDelete={() => resetA4("carburant_bas", r)} deleteLabel="Réinitialiser" />
            );
          })}
          {fFuelDrop.map((r, i) => {
            const cl = compatLabel(r.kind, r.id, hasFuel);
            return (
              <RuleCard key={`fd-${r.kind}-${r.id}`} testid={`rule-carburant-baisse-${i}`} icon={Fuel} color="#EF4444" active={r.active}
                title="Baisse anormale carburant" sub={`${r.dropPct} % / ${r.dropWin} min · ${r.name || label(r.kind, r.id)}${cl ? ` · ${cl}` : ""}`}
                onEdit={() => setModal({ type: "carburant", editing: { mode: "baisse", scopeKind: r.kind === "global" ? "fleet" : r.kind, scopeId: r.id, dropPct: r.dropPct, dropWin: r.dropWin } })}
                onToggle={() => toggleA4("baisse_carburant", r)} onDelete={() => resetA4("baisse_carburant", r)} deleteLabel="Réinitialiser" />
            );
          })}
          {fDriver.map((r, i) => {
            const cl = compatLabel(r.kind, r.id, hasDriver);
            return (
              <RuleCard key={`dr-${r.kind}-${r.id}`} testid={`rule-conducteur-${i}`} icon={UserRound} color="#F5821F" active={r.active}
                title="Conducteur non identifié" sub={`Après ${r.minutes} min · ${r.name || label(r.kind, r.id)}${cl ? ` · ${cl}` : ""}`}
                onEdit={() => setModal({ type: "conducteur", editing: { scopeKind: r.kind === "global" ? "fleet" : r.kind, scopeId: r.id, minutes: r.minutes } })}
                onToggle={() => toggleA4("conducteur_non_identifie", r)} onDelete={() => resetA4("conducteur_non_identifie", r)} deleteLabel="Réinitialiser" />
            );
          })}

          {fAdv.map((r) => (
            <RuleCard key={`a-${r.id}`} testid={`rule-a9-${r.id}`} icon={Wand2} color="#F5821F" active={r.enabled}
              title={r.name} sub="Règle personnalisée avancée"
              onEdit={onAdvanced} onToggle={() => toggleA9(r)} onDelete={() => delA9(r.id)} />
          ))}
        </div>
      </div>

      {/* Mode avancé + notifications + autres types */}
      <div className="flex flex-col items-center gap-2">
        {canManage && (
          <button onClick={() => setPicker(true)} data-testid="other-types-link" className="text-xs text-muted-foreground hover:text-[#17A2B8] underline">
            Autres types d'alerte (maintenance, capteurs, équipement…)
          </button>
        )}
        {canManage && (
          <button onClick={onAdvanced} data-testid="advanced-mode-link" className="text-xs text-muted-foreground hover:text-[#17A2B8] underline">Options avancées — créer une règle multi-conditions</button>
        )}
      </div>

      {/* Notifications — repliable (retiré de la navigation principale, conservé ici) */}
      {canManage && (
        <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="zone-notifications">
          <button onClick={() => setNotifOpen((o) => !o)} data-testid="notif-section-toggle"
            className="w-full flex items-center justify-between px-4 py-3 border-b border-border text-left">
            <h2 className="font-bold flex items-center gap-2"><Bell className="w-4 h-4 brand-teal" /> Notifications — comment et qui est averti</h2>
            {notifOpen ? <ChevronRight className="w-4 h-4 rotate-90 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </button>
          {notifOpen && <div className="p-4"><NotificationRulesView /></div>}
        </div>
      )}

      {/* ZONE 3 — Sélecteur « Créer une alerte » */}
      {picker && (
        <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6" data-testid="alert-picker" onClick={() => setPicker(false)}>
          <div className="bg-card border border-border rounded-2xl w-full max-w-md my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="font-extrabold text-lg">Quel type d'alerte ?</h2>
              <button onClick={() => setPicker(false)} data-testid="picker-close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="label-mono" data-testid="picker-group-standard">Toujours disponibles</p>
              {PICKS.map((p) => (
                <button key={p.key} onClick={() => openCreate(p.key)} data-testid={`pick-${p.key}`} className="w-full text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 flex gap-3 items-center">
                  <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${p.color}26` }}><p.icon className="w-5 h-5" style={{ color: p.color }} /></span>
                  <span><span className="font-bold text-sm block">{p.title}</span><span className="text-xs text-muted-foreground">{p.desc}</span></span>
                  <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                </button>
              ))}
              <button onClick={() => { setPicker(false); setMaintWizard(true); }} data-testid="pick-maintenance" className="w-full text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 flex gap-3 items-center">
                <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#F5821F26" }}><Wrench className="w-5 h-5" style={{ color: "#F5821F" }} /></span>
                <span><span className="font-bold text-sm block">Maintenance</span><span className="text-xs text-muted-foreground">Être alerté avant une vidange, révision ou autre entretien</span></span>
                <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
              </button>

              {/* SELON ÉQUIPEMENT — carte affichée uniquement si ≥1 véhicule réellement compatible */}
              {(fleetCaps.battery > 0 || fleetCaps.dtc > 0 || fleetCaps.fuel > 0 || fleetCaps.driver > 0) && (
                <>
                  <p className="label-mono pt-2" data-testid="picker-group-equipment">Selon l'équipement</p>
                  {fleetCaps.battery > 0 && (
                    <button onClick={() => openCreate("batterie")} data-testid="pick-batterie" className="w-full text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 flex gap-3 items-center">
                      <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#F5821F26" }}><Battery className="w-5 h-5" style={{ color: "#F5821F" }} /></span>
                      <span><span className="font-bold text-sm block">Batterie faible</span><span className="text-xs text-muted-foreground">Disponible sur {fleetCaps.battery} véhicule{fleetCaps.battery > 1 ? "s" : ""}</span></span>
                      <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                    </button>
                  )}
                  {fleetCaps.dtc > 0 && (
                    <button onClick={() => openCreate("dtc")} data-testid="pick-dtc" className="w-full text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 flex gap-3 items-center">
                      <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#EF444426" }}><AlertTriangle className="w-5 h-5" style={{ color: "#EF4444" }} /></span>
                      <span><span className="font-bold text-sm block">Défaut moteur / DTC</span><span className="text-xs text-muted-foreground">Disponible sur {fleetCaps.dtc} véhicule{fleetCaps.dtc > 1 ? "s" : ""}</span></span>
                      <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                    </button>
                  )}
                  {fleetCaps.fuel > 0 && (
                    <button onClick={() => openCreate("carburant")} data-testid="pick-carburant" className="w-full text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 flex gap-3 items-center">
                      <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#17A2B826" }}><Fuel className="w-5 h-5" style={{ color: "#17A2B8" }} /></span>
                      <span><span className="font-bold text-sm block">Carburant</span><span className="text-xs text-muted-foreground">Disponible sur {fleetCaps.fuel} véhicule{fleetCaps.fuel > 1 ? "s" : ""}</span></span>
                      <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                    </button>
                  )}
                  {fleetCaps.driver > 0 && (
                    <button onClick={() => openCreate("conducteur")} data-testid="pick-conducteur" className="w-full text-left border border-border rounded-xl p-4 hover:border-[#17A2B8] hover:bg-[#17A2B8]/5 flex gap-3 items-center">
                      <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#F5821F26" }}><UserRound className="w-5 h-5" style={{ color: "#F5821F" }} /></span>
                      <span><span className="font-bold text-sm block">Conducteur non identifié</span><span className="text-xs text-muted-foreground">Disponible sur {fleetCaps.driver} véhicule{fleetCaps.driver > 1 ? "s" : ""}</span></span>
                      <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                    </button>
                  )}
                </>
              )}

              <button onClick={() => { setPicker(false); onAdvanced && onAdvanced(); }} data-testid="pick-advanced" className="w-full text-left border border-dashed border-border rounded-xl p-4 hover:border-[#17A2B8] flex gap-3 items-center text-muted-foreground">
                <span className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Wand2 className="w-5 h-5" /></span>
                <span><span className="font-bold text-sm block text-foreground">Autre / règle avancée</span><span className="text-xs">Contact, multi-conditions…</span></span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </button>

              {/* Capacités détectées mais pas encore configurables (préparation A10.5) */}
              {fleetCaps.sos > 0 && (
                <div className="pt-3 mt-1 border-t border-border" data-testid="picker-detected-caps">
                  <p className="label-mono mb-1.5 flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5" /> Capacités détectées</p>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p data-testid="cap-sos">SOS · {fleetCaps.sos} véhicule{fleetCaps.sos > 1 ? "s" : ""} équipé{fleetCaps.sos > 1 ? "s" : ""} (alerte matérielle prioritaire, toujours active)</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
